/**
 * MiniDrillModal – a 2-try repeat-after-me interrupt fired inside a live
 * Teacher-Mode session when the model flags a focus word.
 *
 * Flow:
 *   1. GET /api/teacher/mini-drill?focus=<word>  → returns target+respelling.
 *   2. Learner hears it, taps mic, speaks it.
 *   3. Blob goes to /api/stt, transcript + target go to
 *      /api/teacher/mini-drill/attempt → score label.
 *   4. 2 attempts max; dismiss button resumes the main conversation.
 *
 * Props:
 *   - open        : boolean
 *   - focus       : string   — the target word/phrase
 *   - language    : 'es'|'en' — for Whisper hint
 *   - onClose     : fn — resume main conversation
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Microphone, SpinnerGap, SpeakerHigh, CheckCircle, XCircle, ArrowCounterClockwise,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';
import useRecorder from '@/hooks/useRecorder';

export default function MiniDrillModal({ open, focus, language, onClose }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | playing | recording | scoring | done
  const [attempts, setAttempts] = useState([]); // [{score, label, heard}]
  const [error, setError] = useState('');
  const recorder = useRecorder({ vad: true, onAutoStop: () => stopAndScore() });
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open || !focus) return;
    setCard(null); setAttempts([]); setError(''); setPhase('idle');
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.post('/teacher/mini-drill', { focus });
        setCard(data);
      } catch (e) { setError(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, [open, focus]);

  async function playTarget() {
    if (!card?.target) return;
    setPhase('playing');
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text: card.target, speed: 0.8 }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); setPhase('idle'); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPhase('idle'); };
      audio.play().catch(() => setPhase('idle'));
    } catch { setPhase('idle'); }
  }

  async function startRecord() {
    setError('');
    try {
      await recorder.start();
      setPhase('recording');
    } catch (e) {
      setError(e?.message || 'Mic not available');
      setPhase('idle');
    }
  }

  async function stopAndScore() {
    if (phase !== 'recording') return;
    setPhase('scoring');
    const blob = await recorder.stop();
    if (!blob || blob.size < 400) { setPhase('idle'); return; }
    try {
      // 1. Whisper transcribe
      const form = new FormData();
      form.append('audio', blob, 'clip.webm');
      form.append('language', language || 'es');
      const sttResp = await fetch(`${API}/stt?language=${language || 'es'}`, {
        method: 'POST',
        credentials: 'include',
        headers: getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {},
        body: form,
      });
      const sttJson = await sttResp.json();
      const heard = (sttJson?.text || '').trim();
      // 2. Score
      const { data } = await http.post('/teacher/mini-drill/attempt', {
        target: card.target,
        heard,
      });
      setAttempts((a) => [...a, data]);
      setPhase(attempts.length >= 1 ? 'done' : 'idle');
    } catch (e) {
      setError(apiErrorMessage(e));
      setPhase('idle');
    }
  }

  const bestAttempt = attempts.reduce((best, a) => (!best || a.score > best.score ? a : best), null);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50"
            data-testid="mini-drill-backdrop"
          />
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-x-4 top-1/2 z-50 -translate-y-1/2 rounded-3xl bg-[var(--lf-surface)] p-5 shadow-2xl"
            data-testid="mini-drill-modal"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">Quick drill</div>
                <div className="text-lg font-bold mt-0.5">Repeat after me</div>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--lf-surface-muted)]" data-testid="mini-drill-close" aria-label="Close drill">
                <X size={14} aria-hidden="true" />
              </button>
            </div>

            {loading ? (
              <div className="mt-5 text-center text-sm text-[var(--lf-text-muted)]">
                <SpinnerGap size={18} className="inline animate-spin" /> Loading drill…
              </div>
            ) : card ? (
              <div className="mt-4">
                <div className="rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface-muted)] p-4 text-center">
                  <div className="text-2xl font-bold" data-testid="mini-drill-target">{card.target}</div>
                  {card.respelling ? (
                    <div className="mt-1 text-xs text-[var(--lf-text-muted)] font-mono">
                      {card.respelling}{card.ipa ? ` · /${card.ipa}/` : ''}
                    </div>
                  ) : null}
                  {card.example_target ? (
                    <div className="mt-2 italic text-sm">“{card.example_target}”</div>
                  ) : null}
                  <button
                    onClick={playTarget}
                    disabled={phase === 'playing'}
                    className="lf-btn lf-btn-secondary mt-3 w-full py-2 text-sm"
                    data-testid="mini-drill-play-btn"
                  >
                    {phase === 'playing' ? <SpinnerGap size={14} className="animate-spin" /> : <SpeakerHigh size={14} weight="fill" />}
                    Hear it
                  </button>
                </div>

                {attempts.length > 0 ? (
                  <div className="mt-3 space-y-1" data-testid="mini-drill-attempts">
                    {attempts.map((a, i) => (
                      <AttemptRow key={i} idx={i} attempt={a} />
                    ))}
                  </div>
                ) : null}

                <div className="mt-4">
                  {phase === 'done' ? (
                    <div className="flex flex-col gap-2">
                      <div className={`text-center text-sm font-bold ${bestAttempt?.score >= 0.7 ? 'text-[var(--lf-success)]' : 'text-[var(--lf-accent)]'}`}>
                        {bestAttempt?.score >= 0.7 ? '🎉 Nice work!' : 'Good effort — keep practicing!'}
                      </div>
                      <button onClick={onClose} className="lf-btn lf-btn-primary w-full" data-testid="mini-drill-done-btn">
                        Back to conversation
                      </button>
                    </div>
                  ) : phase === 'recording' ? (
                    <button
                      onClick={stopAndScore}
                      className="lf-btn lf-btn-primary w-full bg-red-500"
                      data-testid="mini-drill-stop-btn"
                    >
                      <SpinnerGap size={14} className="animate-spin" /> Listening… tap to stop
                    </button>
                  ) : phase === 'scoring' ? (
                    <div className="lf-btn lf-btn-secondary w-full" data-testid="mini-drill-scoring">
                      <SpinnerGap size={14} className="animate-spin" /> Scoring…
                    </div>
                  ) : (
                    <button
                      onClick={startRecord}
                      className="lf-btn lf-btn-primary w-full"
                      data-testid="mini-drill-record-btn"
                    >
                      {attempts.length > 0
                        ? <><ArrowCounterClockwise size={14} weight="bold" /> Try again ({attempts.length}/2)</>
                        : <><Microphone size={14} weight="fill" /> I'll try</>}
                    </button>
                  )}
                </div>

                {error ? <div className="mt-2 text-xs text-[var(--lf-destructive)] text-center">{error}</div> : null}
              </div>
            ) : null}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function AttemptRow({ idx, attempt }) {
  const ok = attempt.score >= 0.7;
  return (
    <div
      className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${ok ? 'bg-[#ECFDF5] text-[var(--lf-success)]' : 'bg-[#FFF5EE] text-[var(--lf-accent)]'}`}
      data-testid={`mini-drill-attempt-${idx}`}
    >
      {ok ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}
      <span className="font-semibold">{attempt.label}</span>
      <span className="ml-auto opacity-80 italic">"{attempt.heard || '(silence)'}"</span>
    </div>
  );
}
